import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Copy, TerminalWindow } from "@/components/ui/icons";
import {
  DARK_THEME,
  LIGHT_THEME,
  highlightCode,
  resolveShikiLanguage,
} from "@/lib/code-highlight";

interface CodeBlockProps {
  /** The plain-text contents of the block. Used for copy + send-to-shell. */
  text: string;
  /** Optional language tag from the markdown fence (e.g. "ts", "python", "bash"). */
  language: string | null;
  /** Original `<code className="language-...">` children (for token rendering). */
  children: React.ReactNode;
}

/**
 * Visually-distinct code-block wrapper rendered by `MarkdownRenderer`.
 *
 * Replaces the previous bare `<code>` element (no bg / no border / no
 * header) with a framed container that matches what users expect from
 * Slack / Linear / Discord / GitHub:
 *
 *   - 1px tinted border + slightly darker bg → clearly distinct from
 *     surrounding message text.
 *   - Language badge top-left (display name mapped from the fence tag).
 *   - Copy + Send-to-Shell buttons top-right, fade in on hover.
 *   - Horizontal-scrolls for long lines instead of wrapping.
 *   - Keeps the existing drag-to-terminal payload on the inner `<code>`.
 *
 * Phase 1 — no syntax highlighting yet. Phase 2 will lazy-load Shiki
 * for VS-Code-style token coloring; the framing alone is already a
 * major step up from the unstyled current state.
 */
export function CodeBlock({ text, language, children }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const displayLang = formatLanguage(language);
  const isShellLike =
    language === "bash" ||
    language === "sh" ||
    language === "shell" ||
    language === "zsh";
  // Strip the trailing newline that markdown fences always include —
  // copy / drag payloads shouldn't carry it (Slack / Linear / GitHub
  // all behave this way; pasting into a shell would otherwise execute
  // an extra empty line). Visual rendering keeps the raw children.
  const copyText = text.replace(/\n+$/, "");
  const hasCopyPayload = copyText.length > 0;

  // Phase 2: Shiki syntax highlighting — async, lazy-loaded, cached
  // by (theme, language, code). On first render we show the plain
  // `children` fallback so users see content immediately; once Shiki
  // resolves the highlighted HTML takes over.
  //
  // Stored as `{ key, html }` so when the input changes (different
  // text / lang / theme) we discard the now-stale highlight without
  // calling setState synchronously inside the effect body — render
  // just compares `entry.key` against the current key and ignores
  // mismatches. Avoids the cascading-render anti-pattern.
  const shikiLang = resolveShikiLanguage(language);
  const theme = useDocumentTheme();
  const currentKey =
    shikiLang && copyText.length > 0
      ? `${theme}::${shikiLang}::${copyText}`
      : null;
  const [highlightEntry, setHighlightEntry] = useState<{
    key: string;
    html: string;
  } | null>(null);
  useEffect(() => {
    if (!currentKey || !shikiLang) return;
    let cancelled = false;
    void highlightCode(copyText, shikiLang, theme)
      .then((html) => {
        if (cancelled) return;
        setHighlightEntry({ key: currentKey, html });
      })
      .catch(() => {
        // Shiki rejects when a grammar fails to load (network failure,
        // malformed code, etc.). Stay on the plain-children fallback —
        // user still sees their code, just without coloring.
      });
    return () => {
      cancelled = true;
    };
  }, [currentKey, shikiLang, copyText, theme]);
  const highlightedHtml =
    highlightEntry && highlightEntry.key === currentKey
      ? highlightEntry.html
      : null;

  const handleCopy = async () => {
    if (!hasCopyPayload) return;
    try {
      await copyToClipboard(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* navigator.clipboard may be undefined in non-secure contexts */
    }
  };

  return (
    <div
      className={cn(
        "group relative my-2 w-full max-w-[34rem] rounded-lg bg-foreground/[0.03]",
        "transition-colors",
      )}
    >
      <div className="flex items-center justify-between border-b border-foreground/10 px-3 py-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
          {displayLang}
        </span>
        {hasCopyPayload ? (
          <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            {isShellLike ? (
              <Button
                type="button"
                variant="ghost"
                size="tiny"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(
                    "application/x-terminal-command",
                    copyText,
                  );
                  e.dataTransfer.setData("text/plain", copyText);
                  e.dataTransfer.effectAllowed = "copy";
                }}
                onClick={handleCopy}
                className="h-auto gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground"
                title="Drag to terminal — or click to copy as command"
              >
                <TerminalWindow size={11} />
                Shell
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="tiny"
              onClick={handleCopy}
              className="h-auto gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground"
              title="Copy code"
            >
              <Copy size={11} />
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        ) : null}
      </div>
      {highlightedHtml ? (
        // Shiki returns a self-contained `<pre><code>...</code></pre>`.
        // We wrap it in a div that overrides shiki's own bg (we have
        // our own frame already) and forwards the drag payload via
        // event delegation — matches the plain-fallback behavior.
        <div
          className="shiki-host overflow-x-auto px-3 py-2 text-[12.5px] leading-relaxed"
          draggable={hasCopyPayload}
          onDragStart={(e) => {
            if (!hasCopyPayload) {
              e.preventDefault();
              return;
            }
            e.dataTransfer.setData("application/x-terminal-command", copyText);
            e.dataTransfer.setData("text/plain", copyText);
            e.dataTransfer.effectAllowed = "copy";
          }}
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />
      ) : (
        <pre className="overflow-x-auto px-3 py-2 text-[12.5px] leading-relaxed">
          <code
            className={cn(
              "block whitespace-pre font-mono text-foreground/90",
              language ? `language-${language}` : null,
            )}
            draggable={hasCopyPayload}
            onDragStart={(e) => {
              if (!hasCopyPayload) {
                e.preventDefault();
                return;
              }
              e.dataTransfer.setData(
                "application/x-terminal-command",
                copyText,
              );
              e.dataTransfer.setData("text/plain", copyText);
              e.dataTransfer.effectAllowed = "copy";
            }}
          >
            {children}
          </code>
        </pre>
      )}
    </div>
  );
}

/**
 * React-friendly hook that returns the active theme name (dark vs
 * light). Reads `data-theme` from `<html>` if set, falls back to
 * `prefers-color-scheme` media query.
 */
function useDocumentTheme(): typeof DARK_THEME | typeof LIGHT_THEME {
  const [isDark, setIsDark] = useState<boolean>(() => detectDark());
  useEffect(() => {
    // Watch for runtime theme switches via data-theme on <html>.
    const observer = new MutationObserver(() => setIsDark(detectDark()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class"],
    });
    // Also watch the OS-level prefers-color-scheme as a fallback for
    // themes that don't explicitly set data-theme. `matchMedia` may
    // be undefined in test environments (jsdom), so guard.
    const mq =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
    const onChange = () => setIsDark(detectDark());
    mq?.addEventListener("change", onChange);
    return () => {
      observer.disconnect();
      mq?.removeEventListener("change", onChange);
    };
  }, []);
  return isDark ? DARK_THEME : LIGHT_THEME;
}

/**
 * Explicit dark/light classification of Ano's app themes. Naive
 * "contains 'light'" string matching gets `crystal` wrong (it's
 * "colorless liquid glass — all white", i.e. light, despite the
 * absence of "light" in its name).
 */
const LIGHT_APP_THEMES = new Set<string>(["matcha2-light", "crystal"]);

function detectDark(): boolean {
  const dataTheme = document.documentElement.getAttribute("data-theme");
  if (dataTheme) return !LIGHT_APP_THEMES.has(dataTheme);
  if (typeof window.matchMedia !== "function") return true; // test env
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

const LANGUAGE_DISPLAY: Record<string, string> = {
  ts: "TypeScript",
  tsx: "TypeScript",
  typescript: "TypeScript",
  js: "JavaScript",
  jsx: "JavaScript",
  javascript: "JavaScript",
  py: "Python",
  python: "Python",
  rb: "Ruby",
  ruby: "Ruby",
  go: "Go",
  rs: "Rust",
  rust: "Rust",
  java: "Java",
  kt: "Kotlin",
  swift: "Swift",
  c: "C",
  cpp: "C++",
  cs: "C#",
  php: "PHP",
  sh: "Shell",
  bash: "Shell",
  shell: "Shell",
  zsh: "Shell",
  json: "JSON",
  yaml: "YAML",
  yml: "YAML",
  toml: "TOML",
  html: "HTML",
  css: "CSS",
  scss: "Sass",
  sql: "SQL",
  md: "Markdown",
  markdown: "Markdown",
  diff: "Diff",
  graphql: "GraphQL",
  gql: "GraphQL",
  dockerfile: "Dockerfile",
  makefile: "Makefile",
};

function formatLanguage(lang: string | null): string {
  if (!lang) return "Code";
  const lower = lang.toLowerCase();
  return LANGUAGE_DISPLAY[lower] ?? lower;
}
